import { useEffect, useRef, useState } from "react";

/**
 * useMicLevel
 * Monitors the microphone input level and returns boolean
 * @param threshold - RMS threshold (e.g., 0.01)
 * @returns boolean
 */
export function useIsUserSpeaking(threshold = 0.01): boolean {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number>();
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let audioContext: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;

    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      streamRef.current = stream;
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.fftSize);

      function checkLevel() {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const val = (data[i] - 128) / 128;
          sum += val * val;
        }
        // rms = root mean squeare = average potency of the mic
        const rms = Math.sqrt(sum / data.length);

        setIsSpeaking(rms > threshold);
        rafRef.current = requestAnimationFrame(checkLevel);
      }

      checkLevel();
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (analyserRef.current) analyserRef.current.disconnect();
      if (source) source.disconnect();
      if (audioContext) audioContext.close();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [threshold]);

  return isSpeaking;
}
